import './Chart.scss'
import {Chart} from "react-chartjs-2";
import {useRef, useState} from "react";
import ChartDuration from "./ChartDuration.jsx";
import ChartFocus from "./ChartFocus.jsx";
import {useChartData} from "./_useChartData.js";
import {useChillEffect} from "../../../util/mounting-hooks.js";
import {isMobile} from "../../../util/misc.js";
import {useSelector} from "react-redux";
import {CHART_PALETTE, getChartGradients} from "./_chart-palette.js";
import {getIsAthGolden, getIsZeroBaseValue, getShowLatestValue, getTransactionHoverDate, getTransactionHoverType, getTransactionHoverAssetCode, getTransactionHoverAssetCodeFrom, getTransactionHoverIsProfit, getShowTxAnnotations, getShowTxAnnotationsDividends} from "../../../ducks/chart-duck.js";
import {LoaderWrapperCircle} from "../../util/loaders/LoaderWrapper.jsx";
import {getOptions} from "./_options.js";
import {scrollToTop, useNavigation} from "../../util/navigation/navigation.js";
import {APP_ROUTES} from "../../../App.jsx";
import AlertCircleOutlineIcon from "mdi-react/AlertCircleOutlineIcon.js";
import {getCurrentAssetId, getDateFilter, getFilterPreset, getAllAssets} from "../../../ducks/assets-duck.js";
import {useAnnotations} from "../../../api/use-annotations-api.js";
import {activateChartMouseTracker} from "./_overlay.js";
import DiffTracker from "./DiffTracker";
import {useLocation} from "react-router-dom";
import {getTransactionsFiltered} from "../../../ducks/transactions-duck.js";
import {getTransactionParams} from "../transactions/_helpers.js";

const durationToLineThickness = daysDuration => {
    if (daysDuration < 33) {
        return 5
    }
    if (daysDuration < 364) {
        return 4
    }
    if (daysDuration < 364 * 2) {
        return 3
    }
    return 2
}


export default () => {

    const dateFilter = useSelector(getDateFilter)
    const isLatestLineVisible = useSelector(getShowLatestValue)
    const isZeroBaseValue = useSelector(getIsZeroBaseValue)
    const isAthGolden = useSelector(getIsAthGolden)
    const assetId = useSelector(getCurrentAssetId)
    const filterPreset = useSelector(getFilterPreset)
    const transactionHoverDate = useSelector(getTransactionHoverDate)
    const transactionHoverType = useSelector(getTransactionHoverType)
    const transactionHoverAssetCode = useSelector(getTransactionHoverAssetCode)
    const transactionHoverAssetCodeFrom = useSelector(getTransactionHoverAssetCodeFrom)
    const {navigateTo} = useNavigation(scrollToTop)
    const transactionHoverIsProfit = useSelector(getTransactionHoverIsProfit)
    const showTxAnnotations = useSelector(getShowTxAnnotations)
    const showTxAnnotationsDividends = useSelector(getShowTxAnnotationsDividends)
    const location = useLocation()
    const isOnTransactionsTab = location.pathname.includes('/transactions')
    const transactions = useSelector(getTransactionsFiltered)
    const allAssets = useSelector(getAllAssets)
    const {annotations: annotationsRaw} = useAnnotations()

    const chartRef = useRef()
    useChillEffect(()=>{
        if(chartRef.current){
            activateChartMouseTracker(chartRef.current.ctx.canvas)
        }
    }, [chartRef.current])
    const {
        labels,
        values,
        isATH,
        isLatestMovePositive,
        onMouseMove,
        onMouseOver,
        onMouseOut,
        isHistoryFullyLoaded,
        isHovering,
        hoverDate,
    } = useChartData()

    const [data, setData] = useState({labels: [], datasets: []})
    useChillEffect(() => {

        let palette = isLatestMovePositive ? CHART_PALETTE.POSITIVE : CHART_PALETTE.NEGATIVE
        if (isATH && isAthGolden) {
            palette = CHART_PALETTE.GOLDEN
        }

        const {
            lineGradient,
            annotationGradient,
            backgroundGradient,
            latestValueGradient,
        } = getChartGradients(palette, chartRef)

        const lineThickness = durationToLineThickness(values.length)

        setData({
            labels: labels,
            datasets: [
                {
                    label: 'Values',
                    data: values,
                    borderColor: lineGradient,
                    backgroundColor: backgroundGradient,
                    pointRadius: 0,
                    borderWidth: isMobile() ? lineThickness / 4 * 3 : lineThickness,
                    pointHoverRadius: 8,
                    pointHoverBorderWidth: 2,
                    pointHoverBorderColor: 'white',
                    pointHoverBackgroundColor: backgroundGradient,
                    lineTension: 0.15,
                    fill: true,
                },
                isLatestLineVisible && {
                    label: 'Latest',
                    data: values.map(() => values[0]),
                    borderColor: latestValueGradient,
                    backgroundColor: 'transparent',
                    borderDash: [10, 5],
                    pointRadius: 0,
                    pointHoverRadius: 6,
                    pointHoverBorderWidth: 1.5,
                    fill: false,
                },
            ].filter(dataset => !!dataset),
            palette: {
                lineGradient,
                backgroundGradient,
                annotationGradient,
            }
        })

        !isHovering && onMouseOut()

    }, [values.toString(), isLatestLineVisible, isAthGolden, isZeroBaseValue])

    const {
        palette = {},
        // eslint-disable-next-line
        labels: [],
    } = data


    const oldestLabel = labels[labels.length - 1]
    const newestLabel = labels[0]
    const rateMap = Object.fromEntries(allAssets.map(a => [a.id, a.currentRate || 0]))
    const TX_DISPLAY_LIMIT = isMobile() ? 5 : 10
    const TX_PREFILTER_THRESHOLD = 15
    const chartTransactions = (isOnTransactionsTab && showTxAnnotations)
        ? (() => {
            const inRange = transactions
                .filter(tx => tx.date >= oldestLabel && tx.date <= newestLabel)
                .filter(tx => {
                    if (showTxAnnotationsDividends) return true
                    const {verb, isSwap} = getTransactionParams(tx)
                    const type = isSwap ? 'Swap' : verb
                    return type !== 'Dividend' && type !== 'Burn'
                })
                .map(tx => {
                    const {verb, isSwap} = getTransactionParams(tx)
                    const gain = rateMap[tx.fund_id] * tx.amount - tx.cost
                    const gainChild = tx.childTx
                        ? rateMap[tx.childTx.fund_id] * tx.childTx.amount - tx.childTx.cost
                        : 0
                    const realGain = gainChild ? gainChild + gain : gain
                    return {
                        id: tx.id,
                        date: tx.date,
                        type: isSwap ? 'Swap' : verb,
                        assetCode: tx.asset_code,
                        assetCodeFrom: isSwap ? tx.childTx?.asset_code : undefined,
                        isProfit: realGain >= 0,
                        cost: Math.abs(tx.cost),
                    }
                })
            const prefiltered = inRange.length > TX_PREFILTER_THRESHOLD
                ? [...inRange].sort((a, b) => b.cost - a.cost).slice(0, TX_PREFILTER_THRESHOLD)
                : inRange
            return prefiltered.slice(0, TX_DISPLAY_LIMIT)
        })()
        : []

    const options = getOptions({
        onMouseLeave: onMouseOut,
        onMouseEnter: onMouseOver,
        onMouseMove,
        onClick: () => navigateTo(APP_ROUTES.applyHoverDateFilter),
        isZeroBaseValue,
        showDates: false,
        chart: chartRef,
        palette,
        labels,
        dateFilter,
        assetId,
        filterPreset,
        annotationsRaw,
        isHovering,
        hoverDate,
        transactionHoverDate,
        transactionHoverType,
        transactionHoverAssetCode,
        transactionHoverAssetCodeFrom,
        transactionHoverIsProfit,
        chartTransactions,
        values,
    })

    return (
        <div className='chart'>
            <div className='chat-options'>
                <ChartFocus/>
                <ChartDuration/>
            </div>
            <LoaderWrapperCircle
                isLoading={!isHistoryFullyLoaded}
                isPositive={isLatestMovePositive}
            >
                {data.datasets[0]?.data.length <= 1 ?
                    <div className='no-data'>
                        <canvas/>
                        <div className={'no-data-desc'}>
                            <AlertCircleOutlineIcon/>
                            <div className='text'>
                                no data
                            </div>
                        </div>
                    </div>
                    :
                    <div className='chart-wrapper'>
                        <Chart
                            className={`no-select ${values.length < 1 ? 'transparent' : ''}`}
                            type='line'
                            data={data}
                            options={options}
                            ref={chartRef}
                            id='main-chart'
                        />
                        <div id='chart-overlay' className={isHovering ? 'is-hovering' : ''}>
                            <svg xmlns="http://www.w3.org/2000/svg">
                                <line
                                    x1="0"
                                    y1="0"
                                    x2="250"
                                    y2="250"
                                    strokeLinecap="round"
                                    strokeDasharray="4 8"
                                    id='chart-overlay-line'
                                />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="4"
                                    id='chart-overlay-circle'
                                />
                                <text
                                    x="40"
                                    y="35"
                                    id='chart-overlay-text'
                                >
                                    <tspan className='days' id='chart-overlay-days'>0</tspan>
                                    <tspan className='suffix'> days</tspan>
                                </text>
                            </svg>
                        </div>
                        <DiffTracker/>
                    </div>
                }
            </LoaderWrapperCircle>
        </div>
    )
}

